import { ChecklistItemOnView } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { ProtocolOnView } from 'presentation/inspection/inspectionOnView';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { ProtocolSectionModel } from 'shared/domain/inspection/inspectionModel';
import { SectionEstimateConfig } from '../../buildReportPageEstimateInput';
import { InspectionPageEstimateInput } from '../buildInspectionPageEstimateInput';
import {
  buildInspectionChecklistFlatNodes,
  FlatChecklistNode,
  formatChecklistIndexPath,
  getSubtreeItems,
  ProtocolPdfNodeType,
} from '../inspectionChecklistTree';
import {
  AnswerConfigState,
  getActiveAnswerPairs,
  isVisibleInChecklist,
  sectionScore,
} from '../inspectionComplianceClassifier';
import {
  AttachmentsCellGeometry,
  compactAttachmentsBlock,
  documentOnViewsToPdfAttachments,
  PdfAttachment,
} from './inspectionPdfAttachments';
import { INSPECTION_PDF_LABELS, INSPECTION_PDF_SAMPLE_VALUES } from './inspectionPdfLabels';
import { prefixedRichText, sectionHeading } from './inspectionPdfTypography';
import {
  pdfColumn,
  PdfContent,
  PdfSpacing,
  pdfSinglePageColumn,
  pdfTable,
  PdfTableCell,
  PDF_TABLE_CELL_PADDING,
  pdfText,
} from './pdf/pdfBlocks';
import { PDF_CONTENT_WIDTH } from './pdf/pdfPage';
import { boldFont, regularFont } from './pdf/pdfTextMetrics';

/** `Lp.` / `Element kontrolowany` / `Wynik` (`COLUMN_WIDTH_RATIOS`). */
const COLUMN_RATIOS = [10, 50, 40];
const RATIO_TOTAL = COLUMN_RATIOS.reduce((total, ratio) => total + ratio, 0);
const SECTION_TITLE_COLSPAN = 2;
const SUBSECTION_INDENT = 8;
const NESTED_CONTENT_MARGIN = 8;
const ISSUE_LINK_MARGIN = { top: 4 };
const SCORE_FONT = regularFont(8);
const HEADER_FONT = boldFont();

const COMPLIANCE_LABELS: Record<ComplianceAnswerOption, string> = {
  [ComplianceAnswerOption.passed]: INSPECTION_PDF_LABELS.passed,
  [ComplianceAnswerOption.notPassed]: INSPECTION_PDF_LABELS.notPassed,
  [ComplianceAnswerOption.notApplicable]: INSPECTION_PDF_LABELS.notApplicable,
  [ComplianceAnswerOption.unset]: INSPECTION_PDF_LABELS.unchecked,
};

const numberFormat = new Intl.NumberFormat('pl-PL');

/** Share of the table width taken by the columns `[from, to)`. */
const cellWidthRatio = (from: number, to: number): number =>
  COLUMN_RATIOS.slice(from, to).reduce((total, ratio) => total + ratio, 0) / RATIO_TOTAL;

const ITEM_CELL_RATIO = cellWidthRatio(1, 2);
const RESULT_CELL_RATIO = cellWidthRatio(2, 3);
const SECTION_TITLE_CELL_RATIO = cellWidthRatio(0, SECTION_TITLE_COLSPAN);

/** `indentedCellPadding`: nested rows move their content right, eight points per level. */
function indentedPadding(depth: number): PdfSpacing {
  const base = PDF_TABLE_CELL_PADDING;
  return { top: base, bottom: base, left: base + depth * SUBSECTION_INDENT, right: base };
}

function cellGeometry(widthRatio: number, depth: number): AttachmentsCellGeometry {
  const { left, right } = indentedPadding(depth);
  return { widthRatio, horizontalPadding: left + right };
}

/** `answerValueRow`: one result line per answer, rich text where the PDF prints rich text. */
class ChecklistAnswerBlocks {
  static block({ config, answer }: AnswerConfigState, width: number): PdfContent {
    if (!answer) {
      return undefined;
    }
    if (answer.isApplicable === false) {
      return config.type === AnswerType.Compliance
        ? pdfText(INSPECTION_PDF_LABELS.notApplicable, width)
        : ChecklistAnswerBlocks.inline(config.label, INSPECTION_PDF_LABELS.notApplicable, width);
    }
    switch (answer.type) {
      case AnswerType.Compliance:
        return pdfText(
          COMPLIANCE_LABELS[answer.selectedOption ?? ComplianceAnswerOption.unset],
          width
        );
      case AnswerType.Numeric:
        return ChecklistAnswerBlocks.numeric({ config, answer }, width);
      case AnswerType.Text:
        return answer.textValue
          ? prefixedRichText(answer.textValue, config.label, width)
          : undefined;
      case AnswerType.OptionSelection:
        return ChecklistAnswerBlocks.optionSelection({ config, answer }, width);
    }
  }

  /** `inlineAnswer`: `Caption: value` on one line, the value as the caption's postfix. */
  private static inline(label: string, value: string, width: number): PdfContent {
    return pdfText(`${label}:`, width, { postfix: value });
  }

  private static numeric({ config, answer }: AnswerConfigState, width: number): PdfContent {
    if (answer?.type !== AnswerType.Numeric || answer.numericValue === undefined) {
      return undefined;
    }
    const value = answer.numericValue;
    const norm = config.type === AnswerType.Numeric ? config : undefined;
    const isOutOfNorm =
      (norm?.normMin != null && value < norm.normMin) ||
      (norm?.normMax != null && value > norm.normMax);
    const valueText = [
      numberFormat.format(value),
      norm?.unit,
      isOutOfNorm ? INSPECTION_PDF_LABELS.outOfNorm : undefined,
    ]
      .filter(Boolean)
      .join(' ');
    return ChecklistAnswerBlocks.inline(config.label, valueText, width);
  }

  private static optionSelection(
    { config, answer }: AnswerConfigState,
    width: number
  ): PdfContent {
    if (answer?.type !== AnswerType.OptionSelection || config.type !== AnswerType.OptionSelection) {
      return undefined;
    }
    const selectedIds = new Set(answer.selectedOptionIds ?? []);
    const labels = config.options
      .filter((option) => selectedIds.has(option._id))
      .map((option) => option.label);
    return labels.length > 0
      ? prefixedRichText(labels.join(', '), config.label, width)
      : undefined;
  }
}

/** The result line(s) the PDF prints for one answer of a controlled element; nothing for an empty answer. */
export function checklistAnswerBlock(pair: AnswerConfigState, width: number): PdfContent {
  return ChecklistAnswerBlocks.block(pair, width);
}

/**
 * `PdfInspectionChecklistTableFactory`: the whole checklist as one three-column table whose header row
 * repeats on every page. Sections are shaded rows (title + score), elements are rows of index, element
 * and result cells, and hidden "not applicable" / "unchecked" elements are skipped.
 */
class ChecklistTableBuilder {
  private readonly nodes: FlatChecklistNode[];
  private readonly registerPositionByIssueProtocolItem: Map<string, number[]>;

  constructor(private readonly input: InspectionPageEstimateInput) {
    this.nodes = buildInspectionChecklistFlatNodes(input.source.inspection);
    this.registerPositionByIssueProtocolItem = new Map();
    input.source.issueRows.forEach(({ issue }, index) => {
      if (issue.protocolItem) {
        const positions = this.registerPositionByIssueProtocolItem.get(issue.protocolItem) ?? [];
        this.registerPositionByIssueProtocolItem.set(issue.protocolItem, [...positions, index + 1]);
      }
    });
  }

  rows(): PdfTableCell[][] {
    return this.nodes.flatMap((node, index) => {
      if (node.type === ProtocolPdfNodeType.section) {
        return this.sectionRows(node, index);
      }
      return isVisibleInChecklist(node.protocolItem, this.input.inspectionData)
        ? [this.itemRow(node)]
        : [];
    });
  }

  private get templateDocuments() {
    return this.input.source.inspectionTemplateDocuments;
  }

  private attachments(
    attachments: PdfAttachment[],
    config: SectionEstimateConfig,
    label: string,
    geometry: AttachmentsCellGeometry,
    width: number
  ): PdfContent {
    return compactAttachmentsBlock(
      {
        attachments,
        config,
        fileMetadata: this.input.fileMetadata,
        label,
        geometry,
      },
      width
    );
  }

  private sectionRows(
    node: Extract<FlatChecklistNode, { type: ProtocolPdfNodeType.section }>,
    index: number
  ): PdfTableCell[][] {
    const { checklistSection, protocolSection, depth, indexPath } = node;
    const { sectionDescriptions, sectionAttachments } = this.input.templateData;
    const title = [formatChecklistIndexPath(indexPath), checklistSection.title]
      .filter(Boolean)
      .join(' ');
    const description = checklistSection.description?.trim() ?? '';

    const sectionRow: PdfTableCell[] = [
      {
        colSpan: SECTION_TITLE_COLSPAN,
        padding: indentedPadding(depth),
        content: (width) => [
          pdfText(title, width, { font: boldFont() }),
          sectionDescriptions && description
            ? prefixedRichText(description, INSPECTION_PDF_LABELS.sectionDescription, width, NESTED_CONTENT_MARGIN)
            : undefined,
          this.attachments(
            documentOnViewsToPdfAttachments(this.templateDocuments.sectionDocuments[checklistSection._id]),
            sectionAttachments,
            INSPECTION_PDF_LABELS.sectionAttachments,
            cellGeometry(SECTION_TITLE_CELL_RATIO, depth),
            width
          ),
        ],
      },
      { content: (width) => [this.sectionScore(index, width)] },
    ];
    const commentRow = this.sectionCommentRow(protocolSection);
    return commentRow ? [sectionRow, commentRow] : [sectionRow];
  }

  private sectionScore(index: number, width: number): PdfContent {
    const items = getSubtreeItems(this.nodes, index).map((node) => node.protocolItem);
    const { passed, notPassed } = sectionScore(items);
    const total = passed + notPassed;
    return total > 0
      ? pdfText(INSPECTION_PDF_LABELS.sectionScore, width, {
          font: SCORE_FONT,
          postfix: `${passed}/${total}`,
        })
      : undefined;
  }

  /**
   * The comment and the attachments of the section filled in during the inspection, across the table. The row
   * exists only when either of them prints something, which is decided before any of them is measured.
   */
  private sectionCommentRow(protocolSection: ProtocolSectionModel | undefined): PdfTableCell[] | undefined {
    if (!protocolSection) {
      return undefined;
    }
    const comment = protocolSection.comment?.trim() ?? '';
    const { sectionAttachments } = this.input.templateData;
    const attachments = documentOnViewsToPdfAttachments(
      this.input.source.inspectionDocuments[protocolSection._id]
    );
    const printsAttachments = sectionAttachments.enabled && attachments.length > 0;
    if (!comment && !printsAttachments) {
      return undefined;
    }
    return [
      {
        colSpan: COLUMN_RATIOS.length,
        content: (width) => [
          comment ? prefixedRichText(comment, INSPECTION_PDF_LABELS.sectionComment, width) : undefined,
          this.attachments(
            attachments,
            sectionAttachments,
            INSPECTION_PDF_LABELS.sectionCommentAttachments,
            cellGeometry(1, 0),
            width
          ),
        ],
      },
    ];
  }

  private itemRow(
    node: Extract<FlatChecklistNode, { type: ProtocolPdfNodeType.item }>
  ): PdfTableCell[] {
    const { checklistItem, protocolItem, depth, indexPath } = node;
    return [
      {
        padding: indentedPadding(depth),
        content: (width) => [
          pdfText(formatChecklistIndexPath(indexPath), width, { font: boldFont() }),
        ],
      },
      {
        padding: indentedPadding(depth),
        content: (width) => this.itemCellContent(checklistItem, depth, width),
      },
      { content: (width) => this.resultCellContent(checklistItem, protocolItem, width) },
    ];
  }

  private itemCellContent(
    checklistItem: ChecklistItemOnView,
    depth: number,
    width: number
  ): PdfContent[] {
    const { controlledElementStandard, controlledElementStandardAttachments } =
      this.input.templateData;
    const summary = checklistItem.summary?.trim() ?? '';
    const norm = checklistItem.norm?.description?.trim() ?? '';
    return [
      summary ? pdfText(summary, width) : undefined,
      controlledElementStandard && norm
        ? prefixedRichText(norm, INSPECTION_PDF_LABELS.appliedStandard, width, NESTED_CONTENT_MARGIN)
        : undefined,
      this.attachments(
        documentOnViewsToPdfAttachments(this.templateDocuments.checklistDocuments[checklistItem._id]),
        controlledElementStandardAttachments,
        INSPECTION_PDF_LABELS.standardAttachments,
        cellGeometry(ITEM_CELL_RATIO, depth),
        width
      ),
    ];
  }

  private resultCellContent(
    checklistItem: ChecklistItemOnView,
    protocolItem: ProtocolOnView,
    width: number
  ): PdfContent[] {
    const comment = protocolItem.comment?.trim() ?? '';
    return [
      ...getActiveAnswerPairs(checklistItem, protocolItem).map((pair) =>
        checklistAnswerBlock(pair, width)
      ),
      ...this.issueLinks(protocolItem, width),
      comment
        ? prefixedRichText(comment, INSPECTION_PDF_LABELS.comment, width, NESTED_CONTENT_MARGIN)
        : undefined,
      this.attachments(
        documentOnViewsToPdfAttachments(this.input.source.inspectionDocuments[protocolItem._id]),
        this.input.inspectionData.controlledElementAttachments,
        INSPECTION_PDF_LABELS.answerAttachments,
        cellGeometry(RESULT_CELL_RATIO, 0),
        width
      ),
    ];
  }

  /** `issueRegisterLinks`: `→ zgłoszenie #code · Poz. N`, the position only while the register is printed. */
  private issueLinks(protocolItem: ProtocolOnView, width: number): PdfContent[] {
    const positions = this.registerPositionByIssueProtocolItem.get(protocolItem._id) ?? [];
    return positions.map((position) => {
      const registerReference = this.input.parts.issueRegister
        ? ` · ${INSPECTION_PDF_LABELS.registerPosition} ${position}`
        : '';
      return pdfText(
        `→ ${INSPECTION_PDF_LABELS.issueShort} #${INSPECTION_PDF_SAMPLE_VALUES.issueCode}${registerReference}`,
        width,
        { margin: ISSUE_LINK_MARGIN }
      );
    });
  }
}

export function inspectionChecklistSection(
  input: InspectionPageEstimateInput,
  sectionNumber?: number
): PdfContent {
  return pdfColumn([
    pdfSinglePageColumn([sectionHeading(INSPECTION_PDF_LABELS.checklistHeading, sectionNumber)]),
    pdfTable(
      {
        columnRatios: COLUMN_RATIOS,
        headerRow: INSPECTION_PDF_LABELS.checklistColumns.map((caption) => ({
          content: (width) => [pdfText(caption, width, { font: HEADER_FONT })],
        })),
        rows: new ChecklistTableBuilder(input).rows(),
      },
      PDF_CONTENT_WIDTH
    ),
  ]);
}
